import React from 'react';
import AppActionMenu from '../../../../components/AppActionMenu';
import { appToast } from '../../../../components/AppToast';
import { biometricsApi } from '../api/biometricsApi';

export default function BiometricsTable({
  records = [],
  isLoading = false,
  onViewDetail,
  onDelete,
  onResetFilters,
}) {
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Biometric Records...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">fingerprint</span>
          </div>
          <h3 className="text-lg font-bold text-on-surface">No entries meet selected criteria</h3>
          <p className="text-xs text-on-surface-variant max-w-md">
            Either there are no entries in the selected date range or you have selected invalid filter conditions.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-4 py-3.5">IDCard No</th>
              <th className="px-4 py-3.5">Name</th>
              {/* Description = CompanyShortName-DeptName-BranchShortName */}
              <th className="px-4 py-3.5">Co-Dept-Branch</th>
              <th className="px-4 py-3.5 text-center">Date</th>
              <th className="px-4 py-3.5 text-center">First Swipe</th>
              <th className="px-4 py-3.5 text-center">Last Swipe</th>
              {/* M = ManualPunch flag: 'M' if any manual entry, '' if all device */}
              <th className="px-4 py-3.5 text-center">M</th>
              <th className="px-4 py-3.5 text-center">Swipes</th>
              <th className="px-4 py-3.5 text-center">Attendance</th>
              <th className="w-16 px-4 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item, idx) => (
              <tr
                key={item.id ?? `${item.empId}-${item.aDate}-${idx}`}
                className="transition-colors hover:bg-surface-container-low/40 group"
              >
                {/* IDCard No */}
                <td className="px-4 py-3 font-bold font-mono text-xs text-primary">
                  {item.idCardNo}
                </td>

                {/* Name */}
                <td className="px-4 py-3 font-bold text-xs text-on-surface">
                  {item.name}
                </td>

                {/* Co-Dept-Branch (Description) */}
                <td className="px-4 py-3 text-xs text-on-surface-variant max-w-[200px] truncate" title={item.description}>
                  {item.description || '—'}
                </td>

                {/* Date (DD/MM/YYYY from backend) */}
                <td className="px-4 py-3 text-center text-xs font-medium text-on-surface">
                  {item.aDate}
                </td>

                {/* First Swipe */}
                <td className="px-4 py-3 text-center text-xs font-bold text-emerald-600">
                  {item.firstSwipe || '—'}
                </td>

                {/* Last Swipe */}
                <td className="px-4 py-3 text-center text-xs font-bold text-amber-600">
                  {item.lastSwipe || '—'}
                </td>

                {/* ManualPunch Flag */}
                <td className="px-4 py-3 text-center">
                  {item.manualPunch === 'M' ? (
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-extrabold">
                      M
                    </span>
                  ) : (
                    <span className="text-xs text-on-surface-variant/40">—</span>
                  )}
                </td>

                {/* Swipes Count */}
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-surface-container-high text-[11px] font-extrabold text-on-surface">
                    {item.totalSwipes ?? 0}
                  </span>
                </td>

                {/* Attendance */}
                <td className="px-4 py-3 text-center text-xs text-on-surface-variant">
                  {item.attendance || '—'}
                </td>

                {/* Actions */}
                <td className="px-4 py-3 text-right">
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                    items={[
                      { key: 'view_logs', label: 'View Swipe Logs', icon: 'visibility' },
                      { key: 'delete', label: 'Delete Record', icon: 'delete', destructive: true },
                    ]}
                    onSelect={(key) => {
                      if (key === 'view_logs') {
                        onViewDetail?.(item);
                      } else if (key === 'delete') {
                        onDelete?.(item.id, item.name);
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
